import React, { Fragment, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { useUserChannelsStore } from '../../../../stores/userChannelsStore';
import { useAuthStore } from '../../../../stores/authStore';
import { getWebSocketClient } from '../../../../lib/websocket/boardWebSocket';
import { Channel } from '../../../../lib/api/channels';
import { Spinner } from '../../../../components/LogoLoader';

interface SelectChannelSourceModalProps {
    isOpen: boolean;
    onClose: () => void;
    onBack: () => void;
    onSelectChannel: (channel: Channel) => void;
    onAddNew: () => void;
    boardChannels?: Channel[];
}

export const SelectChannelSourceModal: React.FC<SelectChannelSourceModalProps> = ({
    isOpen,
    onClose,
    onBack,
    onSelectChannel,
    onAddNew,
    boardChannels = []
}) => {
    const { t } = useTranslation("common");
    const { channels: userChannels, isLoading, loadChannels, updateChannel } = useUserChannelsStore();
    const syncStatusMessages = useUserChannelsStore(state => state.syncStatusMessages);
    const token = useAuthStore(state => state.tokens?.accessToken);
    const refreshTokens = useAuthStore(state => state.refreshTokens);

    useEffect(() => {
        if (isOpen) {
            loadChannels();
        }
    }, [isOpen, loadChannels]);

    // WebSocket setup for real-time channel sync status updates
    useEffect(() => {
        if (!isOpen || !token) return;

        let wsClient: ReturnType<typeof getWebSocketClient> | null = null;
        const getFreshToken = async (): Promise<string> => {
            try {
                const newTokens = await refreshTokens();
                return newTokens.accessToken;
            } catch (error) {
                console.error('[SelectChannelSourceModal] Failed to refresh token:', error);
                throw error;
            }
        };

        try {
            wsClient = getWebSocketClient(token, getFreshToken);
            wsClient.connect().catch(error => console.error(error));

            const handleChannelSynced = (data: any) => {
                updateChannel(data.channelId, { status: 'active' as const });
                useUserChannelsStore.getState().setSyncStatusMessage(data.channelId, "");
                loadChannels(true);
            };

            const handleChannelSyncStatus = (data: any) => {
                if (data.channelId && data.message) {
                    useUserChannelsStore.getState().setSyncStatusMessage(data.channelId, data.message);
                }
            };

            const handleChannelSyncFailed = (data: any) => {
                useUserChannelsStore.getState().setSyncStatusMessage(data.channelId, "");
                loadChannels(true);
            };

            wsClient.on('channel:synced', handleChannelSynced);
            wsClient.on('channel:sync_status', handleChannelSyncStatus);
            wsClient.on('channel:sync_failed', handleChannelSyncFailed);

            return () => {
                if (wsClient) {
                    wsClient.off('channel:synced', handleChannelSynced);
                    wsClient.off('channel:sync_status', handleChannelSyncStatus);
                    wsClient.off('channel:sync_failed', handleChannelSyncFailed);
                }
            };
        } catch (error) {
            console.error(error);
        }
    }, [isOpen, token, refreshTokens, updateChannel, loadChannels]);

    // Filter out channels already on the board
    const boardChannelIds = new Set(boardChannels.map(bc => bc.id));

    // Render channel icon (same logic as ChannelSelectionView)
    const renderChannelIcon = (channel: Channel) => {
        if (channel.icon && (channel.icon.startsWith("http") || channel.icon.startsWith("/"))) {
            return <img src={channel.icon} alt={channel.name} className="w-6 h-6 object-contain" />;
        }
        if (channel.channelTypeConfig?.icon && (channel.channelTypeConfig.icon.startsWith("/") || channel.channelTypeConfig.icon.startsWith("http"))) {
            return <img src={channel.channelTypeConfig.icon} alt={channel.name} className="w-6 h-6 object-contain" />;
        }
        if (channel.icon) {
            return <span className="text-lg leading-none">{channel.icon}</span>;
        }
        return (
            <span className="w-5 h-5 text-accent-primary" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_4e2f2f0b.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_4e2f2f0b.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
        );
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="duration-0"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="duration-0"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-neutral-900 border border-transparent dark:border-neutral-800 shadow-xl transition-all">
                                <div className="relative p-4 md:p-6">
                                    {/* Back button */}
                                    <button
                                        onClick={() => {
                                            onClose();
                                            onBack();
                                        }}
                                        className="absolute top-4 left-4 p-2 text-neutral-400 hover:text-neutral-500 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-full transition-all focus:outline-none"
                                    >
                                        <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_16a3143a.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_16a3143a.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                    </button>

                                    {/* Close button - behaves as Back to previous modal */}
                                    <button
                                        onClick={() => {
                                            onClose();
                                            onBack();
                                        }}
                                        className="absolute top-4 right-4 p-2 text-neutral-400 hover:text-neutral-500 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-full transition-all focus:outline-none"
                                    >
                                        <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_350784a5.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_350784a5.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                    </button>

                                    {isLoading ? (
                                        <div className="flex items-center justify-center py-12">
                                            <Spinner size="md" className="text-accent-primary" />
                                        </div>
                                    ) : userChannels.length > 0 ? (
                                        <>
                                            <Dialog.Title className="text-xl font-bold text-neutral-900 dark:text-neutral-100 mb-2 text-center">
                                                {t('boards.selectChannel', 'Select Channel')}
                                            </Dialog.Title>
                                            <p className="text-sm text-neutral-600 dark:text-neutral-400 text-center mb-6">
                                                {t('boards.selectChannelToAdd', 'Choose a channel to add to your board')}
                                            </p>

                                            <div className="space-y-3 max-h-[60vh] md:max-h-[400px] overflow-y-auto pr-1">
                                                {userChannels.map((channel) => {
                                                    const isAdded = boardChannelIds.has(channel.id);
                                                    const isPseudoChannelEnabled = channel.channelTypeConfig?.pseudoChannelEnabled === true;
                                                    // Pseudo-channel-eligible channels stay selectable even after one
                                                    // pseudo is added — the user can attach more ad accounts.
                                                    const isSelected = isAdded && !isPseudoChannelEnabled;
                                                    const isSyncing = channel.status === 'syncing' || channel.status === 'pending';

                                                    return (
                                                        <button
                                                            key={channel.id}
                                                            onClick={() => !isSelected && !isSyncing && onSelectChannel(channel)}
                                                            disabled={isSelected || isSyncing}
                                                            className={`w-full group bg-white dark:bg-neutral-900 border-2 border-neutral-200 dark:border-neutral-800 rounded-xl p-4 transition-all duration-200 text-left ${isSelected || isSyncing ? 'opacity-50 cursor-not-allowed' : 'hover:border-accent-primary/50 hover:bg-accent-primary/5 dark:hover:bg-accent-primary/10'}`}
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-10 h-10 bg-neutral-100 dark:bg-neutral-800 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-accent-primary/10 transition-colors">
                                                                    {renderChannelIcon(channel)}
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-center gap-2">
                                                                        <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                                                                            {channel.name || channel.channelTypeConfig?.name || channel.channelType}
                                                                        </h3>
                                                                        {isSyncing && (
                                                                            <span className="text-[10px] font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-1.5 py-0.5 rounded-full border border-amber-100 dark:border-amber-800 animate-pulse">
                                                                                {syncStatusMessages?.[String(channel.id)] ? t(`common:${syncStatusMessages[String(channel.id)]}`) : t('common:channels.syncing')}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <p className="text-xs text-neutral-500 dark:text-neutral-400">
                                                                        {channel.channelTypeConfig?.category || channel.channelType}
                                                                    </p>
                                                                </div>
                                                                {isSyncing ? (
                                                                    <Spinner size="sm" className="text-amber-500" />
                                                                ) : isSelected ? (
                                                                    <div className="flex-shrink-0 text-emerald-500">
                                                                        <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_1fc86707.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_1fc86707.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                                    </div>
                                                                ) : isPseudoChannelEnabled ? (
                                                                    <span className="w-5 h-5 text-neutral-400 dark:text-neutral-500 flex-shrink-0" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_39864a1b.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_39864a1b.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                                ) : (
                                                                    <span className="text-sm font-medium text-accent-primary">
                                                                        {t('boards.addToBoard', 'Add to board')}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </button>
                                                    );
                                                })}

                                                {/* Add new channel option - Keep this for when list is not empty but user wants to add another */}
                                                <button
                                                    id="onboarding-add-channel-modal"
                                                    onClick={onAddNew}
                                                    className="w-full group bg-white dark:bg-neutral-900 border-2 border-dashed border-neutral-200 dark:border-neutral-800 rounded-xl p-4 hover:border-accent-primary/50 hover:bg-accent-primary/5 dark:hover:bg-accent-primary/10 transition-all duration-200 flex items-center justify-center gap-2"
                                                >
                                                    <span className="w-5 h-5 text-neutral-400 group-hover:text-accent-primary transition-colors" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_d840adba.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_d840adba.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                    <span className="text-sm font-semibold text-neutral-600 dark:text-neutral-400 group-hover:text-accent-primary transition-colors">
                                                        {t('channels.addChannel', 'Add New Channel')}
                                                    </span>
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        // Empty View / Prompt Modal
                                        <div className="text-center py-6">
                                            <div className="w-16 h-16 bg-neutral-100 dark:bg-neutral-800 rounded-full flex items-center justify-center mx-auto mb-4">
                                                <span className="w-8 h-8 text-neutral-400" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_18e3dc9b.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_18e3dc9b.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                            </div>
                                            <Dialog.Title className="text-xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">
                                                {t('boards.connectFirstChannel', 'Connect a Channel')}
                                            </Dialog.Title>
                                            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-8 max-w-xs mx-auto leading-relaxed">
                                                {t('boards.connectFirstChannelDesc', 'You need to connect a channel (like Plaid or Stripe) before you can add it to your board.')}
                                            </p>
                                            <button
                                                id="onboarding-connect-channel-first"
                                                onClick={onAddNew}
                                                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-accent-primary text-white font-semibold rounded-xl hover:bg-accent-primary-dark transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                                            >
                                                <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_d039aac0.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_d039aac0.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                {t('channels.connectChannel', 'Connect Channel')}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
